
// Revisado: 2025-12-18 I18n Updated
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ROUTES } from '../../../routes';
import { API_BASE_URL } from '../../../constants';
import { useAuth } from '../../../context/AuthContext';
import SupportEdit from './SupportEdit';
import SupportSkillsEdit from './SupportSkillsEdit';
import SupportBonusEdit from './SupportBonusEdit';
import SupportEditEvents from './SupportEditEvents';
import { useTranslation } from 'react-i18next';

const SupportEditPage = () => {
    const { id } = useParams(); // support_id
    const { isAdmin, loading: authLoading } = useAuth();
    const navigate = useNavigate();
    const { t } = useTranslation();

    const [supportData, setSupportData] = useState(null);
    const [skillsData, setSkillsData] = useState([]);
    const [bonusNormal, setBonusNormal] = useState([]);
    const [bonusUnique, setBonusUnique] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // 1. Check Admin (Wait for Auth Loading)
    useEffect(() => {
        if (authLoading) return;
        if (!isAdmin()) {
            navigate(ROUTES.CHARACTERS_SUPPORT);
        }
    }, [isAdmin, authLoading, navigate]);

    // 2. Fetch Data
    const fetchSupportData = useCallback(async () => {
        try {
            setLoading(true);
            const response = await fetch(`${API_BASE_URL}/api/supports/${id}`);
            if (!response.ok) throw new Error('Failed to fetch support card');

            const data = await response.json();
            setSupportData(data.info);
            setSkillsData(data.skills); // Array of skills linked
            setBonusNormal(data.normal_effects || []);
            setBonusUnique(data.unique_effects || []);
            setLoading(false);
        } catch (err) {
            setError(err.message);
            setLoading(false);
        }
    }, [id]);

    useEffect(() => {
        if (id) {
            fetchSupportData();
        }
    }, [id, fetchSupportData]);

    if (authLoading || loading) return <div className="p-8 text-center text-white">{t('SupportEditPage.loading')}</div>;
    if (error) return <div className="p-8 text-center text-red-500">{t('SupportEditPage.error', { error })}</div>;
    if (!supportData || !isAdmin()) return null; // Double safety

    return (
        <div className="max-w-7xl mx-auto p-4 md:p-8 space-y-8">
            <div className="flex items-center justify-between">
                <h1 className="text-3xl font-bold text-white">
                    {t('SupportEditPage.title')} <span className="text-yellow-400">{supportData.name_en || supportData.name_jp}</span>
                </h1>
                <button
                    onClick={() => navigate(`${ROUTES.CHARACTERS_SUPPORT}/${id}`)}
                    className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded transition-colors"
                >
                    {t('SupportEditPage.back_button')}
                </button>
            </div>

            {/* 1. Basic Info Edit */}
            <div className="bg-white rounded-xl shadow-lg overflow-hidden">
                <div className="bg-blue-600 px-6 py-4 border-b border-blue-700">
                    <h2 className="text-xl font-bold text-white">{t('SupportEditPage.section_basic')}</h2>
                </div>
                <div className="p-6">
                    <SupportEdit initialData={supportData} isEditMode={true} onUpdate={fetchSupportData} />
                </div>
            </div>

            {/* 2. Skills Edit */}
            <div className="bg-white rounded-xl shadow-lg">
                <div className="bg-green-600 px-6 py-4 border-b border-green-700 rounded-t-xl">
                    <h2 className="text-xl font-bold text-white">{t('SupportEditPage.section_skills')}</h2>
                </div>
                <div className="p-6">
                    <SupportSkillsEdit
                        supportId={supportData.support_id}
                        currentSkills={skillsData}
                        onUpdate={fetchSupportData}
                    />
                </div>
            </div>

            {/* 3. Bonus Edit Matrix */}
            <div className="bg-white rounded-xl shadow-lg">
                <div className="bg-purple-600 px-6 py-4 border-b border-purple-700 rounded-t-xl">
                    <h2 className="text-xl font-bold text-white">{t('SupportEditPage.section_bonuses')}</h2>
                </div>
                <div className="p-6">
                    <SupportBonusEdit
                        supportId={supportData.support_id}
                        initialNormalEffects={bonusNormal}
                        initialUniqueEffects={bonusUnique}
                        onUpdate={fetchSupportData}
                    />
                </div>
                {/* 4. Events Edit */}
                <div className="bg-white rounded-xl shadow-lg">
                    <div className="bg-orange-600 px-6 py-4 border-b border-orange-700 rounded-t-xl">
                        <h2 className="text-xl font-bold text-white">Events & Options</h2>
                    </div>
                    <div className="p-6">
                        <SupportEditEvents
                            supportId={supportData.support_id}
                            onUpdate={fetchSupportData}
                        />
                    </div>
                </div>

            </div>

        </div>
    );
};

export default SupportEditPage;
